// Shared value editor: adapters preserve separate owner and business API authority.
// The server owns every bound, invariant and validation; drafts retain both edited versions.
import React from "react";
import { Banner, Button, FormField, Input, StatusPill, Switch } from "@/ds";
import { ApiError, cockpit } from "@/api";
import type {
  OwnerTenantRuleParameters,
  OwnerRuleValueDelegation,
  RuleParamKey,
  RuleParamValues,
  RuleParameterDefinition,
  RuleParameterIssue,
  RuleValuePreview,
} from "@/api";
import { useLang, useT } from "../i18n";
import { useSession } from "../session";
import { useLiveInvalidation, useOwnerLiveInvalidation } from "../live/live-provider";
import type { PushToast } from "@/notifications/toast-queue";

import { ruleValueNumber, parseRuleValueInput } from "./rule-parameter-display";
import { TenantRuleDelegation } from "./tenant-rule-delegation";
import { TenantRuleHistory } from "./tenant-rule-history";
import { ownerRuleValuesAdapter, type RuleValuesAdapter, type RuleValuesEditorData, type RuleValuesEdit } from "./rule-values-adapter";
import { BusinessRuleValueField } from "./business-rule-value-field";
import { RuleValuesPreviewDialog } from "./rule-values-preview";

type Draft = Record<RuleParamKey, string | boolean>;

/** #1622: an unsaved draft survives a reload or a language switch. Only the typed values and the
 *  versions they were edited from are kept, per actor, business and editing plane; the server still validates. */
interface StoredDraft {
  baseVersion: number;
  delegationVersion?: number;
  values: Partial<Draft>;
}

function draftStorageKey(actorId: string, tenantId: string): string {
  return `reqventos:rule-parameter-draft:${actorId}:${tenantId}`;
}

/** A stored value is kept only for a registered key and with the type its field edits. */
function readStoredDraft(key: string, parameters: readonly RuleParameterDefinition[]): StoredDraft | null {
  try {
    const value = JSON.parse(window.localStorage.getItem(key) ?? "null");
    const stored = value?.values;
    if (!Number.isSafeInteger(value?.baseVersion) || value.baseVersion < 0
        || !stored || typeof stored !== "object" || Array.isArray(stored)) return null;
    const values: Partial<Draft> = {};
    for (const definition of parameters) {
      const typed = stored[definition.key];
      if (typeof typed === (definition.value_type === "boolean" ? "boolean" : "string")) {
        values[definition.key] = typed;
      }
    }
    return { baseVersion: value.baseVersion, delegationVersion: value.delegationVersion, values };
  } catch { return null; }
}

function storeDraft(key: string, value: StoredDraft | null) {
  try {
    if (value) window.localStorage.setItem(key, JSON.stringify(value));
    else window.localStorage.removeItem(key);
  } catch { /* Storage is optional: without it an unsaved draft lives only in this page. */ }
}

const RISK_TONE: Record<string, "warning" | "danger"> = {
  medium_high: "warning",
  high: "warning",
  critical: "danger",
};

function draftOf(values: RuleParamValues, parameters: readonly RuleParameterDefinition[]): Draft {
  const draft = {} as Draft;
  for (const [key, value] of Object.entries(values) as [RuleParamKey, number | boolean][]) {
    draft[key] = typeof value === "boolean" ? value : String(ruleValueNumber(value, parameters.find((p) => p.key === key)?.unit));
  }
  return draft;
}

/** Typed text becomes a number when it reads as one; anything else is sent as typed so the server
 *  answers with its own type error instead of this form deciding what is valid. */
function valueOf(definition: RuleParameterDefinition, draft: string | boolean): unknown {
  if (definition.value_type === "boolean" || typeof draft === "boolean") return draft;
  return parseRuleValueInput(draft, definition.unit);
}

function changedPatch(data: OwnerTenantRuleParameters, draft: Draft): Partial<RuleParamValues> {
  const patch: Record<string, unknown> = {};
  for (const definition of data.parameters) {
    const next = valueOf(definition, draft[definition.key]);
    if (next !== data.values[definition.key]) patch[definition.key] = next;
  }
  return patch as Partial<RuleParamValues>;
}

function issueCode(error: unknown): string | null {
  return error instanceof ApiError && error.data && typeof error.data === "object"
    ? String((error.data as { code?: unknown }).code ?? "") || null
    : null;
}

export interface TenantRuleParametersEditorProps {
  tenantId: string;
  pushToast: PushToast;
}

export function TenantRuleParametersEditor({ tenantId, pushToast }: TenantRuleParametersEditorProps) {
  const { user } = useSession();
  const adapter = React.useMemo(() => ownerRuleValuesAdapter(tenantId), [tenantId]);
  return <RuleValuesEditor key={`${user?.id}:${tenantId}`} tenantId={tenantId} pushToast={pushToast} adapter={adapter} />;
}

export function RuleValuesEditor({ tenantId, pushToast, adapter }: TenantRuleParametersEditorProps & { adapter: RuleValuesAdapter }) {
  const t = useT();
  const { lang } = useLang();
  const { user } = useSession();
  const business = adapter.plane === "business";
  const storageKey = user?.id ? `${draftStorageKey(user.id, tenantId)}${business ? ":business" : ""}` : null;
  const translator = React.useRef(t);
  translator.current = t;
  const sequence = React.useRef(0);
  const working = React.useRef(false);
  const tenantInvalidations = useLiveInvalidation("settings", "tenants");
  const ownerInvalidations = useOwnerLiveInvalidation("settings", "tenants");
  const invalidations = business ? tenantInvalidations : ownerInvalidations;
  const [remoteChanged, setRemoteChanged] = React.useState(false);
  const [delegationBusy, setDelegationBusy] = React.useState(false);
  const delegationEdit = React.useRef({ dirty: false, busy: false });
  const delegationEditChanged = React.useCallback((dirty: boolean, busy: boolean) => {
    delegationEdit.current = { dirty, busy };
    setDelegationBusy(busy);
  }, []);
  const [preview, setPreview] = React.useState<{ result: RuleValuePreview; input: RuleValuesEdit } | null>(null);
  const [baseDelegationVersion, setBaseDelegationVersion] = React.useState(0);
  const [delegationConflict, setDelegationConflict] = React.useState(false);
  const [tab, setTab] = React.useState("values");
  const [delegation, setDelegation] = React.useState<OwnerRuleValueDelegation | null>(null);
  const [delegationError, setDelegationError] = React.useState<string | null>(null);
  const reloadDelegation = React.useCallback(async () => {
    const mine = sequence.current;
    setDelegationError(null);
    try {
      const next = await cockpit.getTenantRuleDelegation(tenantId);
      if (mine === sequence.current) setDelegation(next);
    } catch (error) {
      if (mine === sequence.current) setDelegationError((error as Error)?.message || "unknown");
    }
  }, [tenantId]);
  const [data, setData] = React.useState<RuleValuesEditorData | null>(null);
  const [draft, setDraft] = React.useState<Draft | null>(null);
  // The version the draft was edited from: behind `data.version` only for a restored stale draft.
  const [baseVersion, setBaseVersion] = React.useState(0);
  const [restored, setRestored] = React.useState(false);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [invalidSet, setInvalidSet] = React.useState(false);
  const [issues, setIssues] = React.useState<RuleParameterIssue[]>([]);
  const [conflictVersion, setConflictVersion] = React.useState<number | null>(null);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const displayed = React.useRef({ data, draft, delegation });
  displayed.current = { data, draft, delegation };

  const show = React.useCallback((next: RuleValuesEditorData) => {
    if (next.delegation) setDelegation(next.delegation);
    setBaseDelegationVersion(next.delegation?.version ?? 0);
    setDelegationConflict(false);
    setPreview(null);
    setData(next);
    setDraft(draftOf(next.values, next.parameters));
    setBaseVersion(next.version);
    setRestored(false);
    setIssues([]);
    setConflictVersion(null);
    setSaveError(null);
    setRemoteChanged(false);
  }, []);

  const readCurrent = React.useCallback(() => Promise.all([
    adapter.load(), business ? Promise.resolve(undefined) : cockpit.getTenantRuleDelegation(tenantId),
  ]), [adapter, business, tenantId]);

  /** `restore` brings back a stored draft; loading after a conflict or a refusal discards it. */
  const load = React.useCallback((restore: boolean) => {
    const mine = ++sequence.current;
    setLoading(true);
    setLoadError(null);
    setInvalidSet(false);
    return readCurrent()
      .then(([next, grants]) => {
        if (mine !== sequence.current) return;
        show(next);
        if (grants) setDelegation(grants);
        setDelegationError(null);
        if (!storageKey) return;
        const stored = restore && next.editable ? readStoredDraft(storageKey, next.parameters) : null;
        const merged = stored ? { ...draftOf(next.values, next.parameters), ...stored.values } : null;
        if (!stored || !merged || Object.keys(changedPatch(next, merged)).length === 0) {
          storeDraft(storageKey, null);
          return;
        }
        setDraft(merged);
        setBaseVersion(stored.baseVersion);
        if (business) {
          setBaseDelegationVersion(stored.delegationVersion ?? -1);
          setDelegationConflict(stored.delegationVersion !== next.delegation?.version);
        }
        setRestored(true);
        // Edited from an older version: the owner sees the edit but reloads before saving.
        if (stored.baseVersion !== next.version) setConflictVersion(next.version);
      })
      .catch((error: unknown) => {
        if (mine !== sequence.current) return;
        if (issueCode(error) === "tenant_rule_parameter_set_invalid") {
          if (storageKey) storeDraft(storageKey, null);
          setInvalidSet(true);
        } else {
          setLoadError(issueCode(error) === "FEATURE_DISABLED"
            ? translator.current("ruleValues.unavailable.body") : (error as Error)?.message || translator.current("common.error"));
        }
      })
      .finally(() => { if (mine === sequence.current) setLoading(false); });
  }, [readCurrent, business, show, storageKey]);

  React.useEffect(() => {
    void load(true);
    return () => { sequence.current++; };
  }, [load]);

  React.useEffect(() => {
    if (invalidations === 0 || loading) return;
    let active = true;
    const mine = sequence.current;
    void readCurrent().then(([next, grants]) => {
      if (!active || mine !== sequence.current) return;
      const current = displayed.current;
      if (!current.data || !current.draft) return;
      const nextDelegation = next.delegation ?? grants;
      if (next.version === current.data.version && next.editable === current.data.editable
          && nextDelegation?.version === current.delegation?.version) return;
      // Inspect the draft when the read finishes: an edit may have started while it was in flight.
      if (Object.keys(changedPatch(current.data, current.draft)).length > 0
          || delegationEdit.current.dirty || delegationEdit.current.busy || working.current) {
        sequence.current++;
        setRemoteChanged(true);
        setPreview(null);
      } else {
        show(next);
        if (nextDelegation) setDelegation(nextDelegation);
      }
    }).catch(() => {
      if (!active || mine !== sequence.current) return;
      // A revoked capability or unavailable read cannot leave a stale editor ready to save.
      sequence.current++;
      setRemoteChanged(true);
      setPreview(null);
      setSaveError(translator.current("businesses.rules.remote.unavailable"));
    });
    return () => { active = false; };
  }, [invalidations, loading, readCurrent, show]);

  // Keep the stored draft in step with the fields: only the fields the owner changed, so a restore
  // over newer values leaves the others as saved; an unchanged form leaves nothing behind.
  React.useEffect(() => {
    if (!storageKey || !data || !draft || !data.editable) return;
    const edited: Partial<Draft> = {};
    for (const key of Object.keys(changedPatch(data, draft)) as RuleParamKey[]) edited[key] = draft[key];
    storeDraft(storageKey, Object.keys(edited).length > 0 ? { baseVersion, delegationVersion: baseDelegationVersion, values: edited } : null);
  }, [baseVersion, baseDelegationVersion, data, draft, storageKey]);

  if (invalidSet) {
    return (
      <Banner tone="danger" title={t("businesses.rules.invalidSet.title")}>
        {t("businesses.rules.invalidSet.body")}
      </Banner>
    );
  }
  if (loadError) return <Banner tone="danger" title={t("common.error")}>
    <p>{loadError}</p><Button variant="secondary" onClick={() => { void load(true); }}>{t("businesses.rules.conflict.reload")}</Button>
  </Banner>;
  if (loading || !data || !draft) return <p className="subtle" aria-busy="true">{t("common.loading")}</p>;

  const patch = changedPatch(data, draft);
  const dirty = Object.keys(patch).length > 0;
  const readOnly = !data.editable;

  const fieldIssues = (key: RuleParamKey): RuleParameterIssue[] =>
    issues.filter((issue) => issue.path[0] === key
      || (issue.code === "invariant_violation"
        && data.invariants.some((inv) => inv.id === issue.invariantId && inv.keys.includes(key))));

  const issueText = (issue: RuleParameterIssue, definition: RuleParameterDefinition): string => {
    switch (issue.code) {
      case "integer_out_of_bounds": {
        if (definition.max_inclusive !== undefined && definition.max_inclusive < Number.MAX_SAFE_INTEGER) {
          return t("businesses.rules.issue.between", { min: ruleValueNumber(definition.min_inclusive ?? 0, definition.unit), max: ruleValueNumber(definition.max_inclusive, definition.unit) });
        }
        // Only the registry's safe-integer ceiling is left above the minimum (e.g. "1e20").
        const typed = Number(String(draft[definition.key]).trim());
        return definition.min_inclusive !== undefined && typed >= definition.min_inclusive
          ? t("businesses.rules.issue.tooLarge")
          : t("businesses.rules.issue.atLeast", { min: ruleValueNumber(definition.min_inclusive ?? 0, definition.unit) });
      }
      case "rule_value_not_delegated": return t("ruleValues.issue.notDelegated");
      case "rule_value_out_of_range": return t("ruleValues.issue.range");
      case "rule_value_off_step": return t("ruleValues.issue.step");
      case "expected_integer":
        if (definition.unit === "eur_cents") return t("businesses.rules.issue.cents");
        return t("businesses.rules.issue.integer");
      case "expected_boolean":
        return t("businesses.rules.issue.boolean");
      case "invariant_violation":
        return t(`businesses.rules.invariant.${issue.invariantId}`, undefined, t("businesses.rules.issue.invariant"));
      default:
        return t("businesses.rules.issue.other", { code: issue.code });
    }
  };

  const refused = (error: unknown) => {
    setPreview(null);
    const code = issueCode(error);
    const body = error instanceof ApiError ? (error.data as Record<string, unknown> | undefined) : undefined;
    if (code === "invalid_rule_parameters" && Array.isArray(body?.issues)) {
      setIssues(body.issues as RuleParameterIssue[]);
    } else if (typeof body?.key === "string" && data.parameters.some((p) => p.key === body.key)) {
      setIssues([{ code: code ?? "unknown", path: [body.key] }]);
    } else if (code === "rule_parameters_changed") {
      setConflictVersion(typeof body?.current_version === "number" ? body.current_version : data.version);
    } else if (code === "rule_delegation_changed") {
      setDelegationConflict(true);
    } else if (code === "tenant_read_only" || code === "FEATURE_DISABLED") {
      void load(false);
    } else if (code === "tenant_rule_parameter_set_invalid") {
      if (storageKey) storeDraft(storageKey, null);
      setInvalidSet(true);
    } else {
      setSaveError((error as Error)?.message || t("common.error"));
    }
  };

  const input = (): RuleValuesEdit => ({ expected_version: baseVersion,
    expected_delegation_version: baseDelegationVersion, values: patch });
  const run = async (action: () => Promise<void>) => {
    if (working.current) return;
    working.current = true;
    setBusy(true);
    setIssues([]);
    setSaveError(null);
    const mine = sequence.current;
    try { await action(); } catch (error) { if (mine === sequence.current) refused(error); }
    finally { working.current = false; setBusy(false); }
  };
  const save = () => {
    if (remoteChanged) return;
    // Business save uses exactly the patch whose server preview the admin confirmed.
    if (business && !preview) return;
    const edited = preview?.input ?? input();
    const mine = sequence.current;
    void run(async () => {
      const next = await adapter.save(edited);
      if (mine !== sequence.current) return;
      if (storageKey) storeDraft(storageKey, null);
      show(next);
      pushToast(next.version === edited.expected_version
        ? { tone: "info", title: t("businesses.rules.unchanged") }
        : { tone: "success", title: t("businesses.rules.saved", { version: next.version }) });
    });
  };
  const check = () => {
    if (!adapter.preview || remoteChanged) return;
    const edited = input();
    const mine = sequence.current;
    void run(async () => {
      const result = await adapter.preview!(edited);
      if (mine !== sequence.current) return;
      setPreview({ result, input: edited });
    });
  };

  // The server's reasons describe the values it was sent; once a field changes they no longer apply.
  const edit = (key: RuleParamKey, next: string | boolean) => {
    setDraft({ ...draft, [key]: next });
    setIssues([]);
    setPreview(null);
    setSaveError(null);
  };

  // Back to the saved values; the stored draft goes with them (see the effect above). After a
  // conflict the saved values shown here are outdated, so reverting loads the current ones.
  const discard = () => {
    if (conflictVersion !== null || delegationConflict || remoteChanged) {
      void load(false);
      return;
    }
    setDraft(draftOf(data.values, data.parameters));
    setIssues([]);
    setRestored(false);
    setPreview(null);
    setSaveError(null);
  };

  const labelOf = (key: RuleParamKey) => t(`businesses.rules.param.${key}`, undefined, key);

  return (
    <div className="stack-sm management-editor reqventos-rule-parameters" data-testid="rule-parameters-editor">
      <div className="neo-tabs__list" role="tablist" aria-label={t("businesses.rules.tab.label")}>
        {(business ? ["values", "history"] : ["values", "delegation", "history"]).map((name, index, tabs) => <button key={name}
          id={`rule-tab-${name}`} type="button" role="tab" aria-selected={tab === name}
          aria-controls={`rule-panel-${name}`} tabIndex={tab === name ? 0 : -1}
          className={`neo-tab ${tab === name ? "neo-tab--active" : ""}`}
          onClick={() => setTab(name)} onKeyDown={(event) => {
            const next = event.key === "ArrowRight" ? tabs[(index + 1) % tabs.length]
              : event.key === "ArrowLeft" ? tabs[(index + tabs.length - 1) % tabs.length]
                : event.key === "Home" ? tabs[0] : event.key === "End" ? tabs[tabs.length - 1] : null;
            if (next) { event.preventDefault(); setTab(next); document.getElementById(`rule-tab-${next}`)?.focus(); }
          }}>{t(`businesses.rules.tab.${name}`)}</button>)}
      </div>
      {readOnly && <Banner tone="info" title={t("businesses.rules.readOnly.title")}>
        {t("businesses.rules.readOnly.body")}
      </Banner>}
      {remoteChanged && <Banner tone="warning" title={t("businesses.rules.remote.title")}>
        <p>{t("businesses.rules.remote.body")}</p>
        <Button variant="secondary" disabled={busy || delegationBusy}
          onClick={() => { void load(false); }}>{t("businesses.rules.remote.reload")}</Button>
      </Banner>}
      <div id="rule-panel-values" role="tabpanel" aria-labelledby="rule-tab-values" hidden={tab !== "values"} className="stack-sm reqventos-rule-panel">
      <p className="subtle reqventos-rule-parameters__intro">{t(business ? "ruleValues.hint" : "businesses.rules.hint")}</p>

      <div className="stack-xs reqventos-detail-pairs">
        <div className="neo-kv">
          <span className="neo-kv__k">{t("businesses.rules.source")}</span>
          <span className="neo-kv__v">
            {data.source === "tenant_set"
              ? t("businesses.rules.source.tenantSet", { version: data.version })
              : t("businesses.rules.source.defaults")}
          </span>
        </div>
      </div>

      {delegationConflict && <Banner tone="warning" title={t("businesses.rules.delegation.conflict")}>
        <p>{t("ruleValues.delegationConflict")}</p>
        <Button variant="secondary" disabled={busy} onClick={() => { void load(false); }}>{t("businesses.rules.conflict.reload")}</Button>
      </Banner>}
      {conflictVersion !== null && (
        <Banner tone="warning" title={t("businesses.rules.conflict.title")}>
          <p style={{ margin: "0 0 var(--space-2)" }}>
            {t("businesses.rules.conflict.body", { version: conflictVersion })}
          </p>
          <Button variant="secondary" size="sm" disabled={busy} onClick={() => { void load(false); }}>
            {t("businesses.rules.conflict.reload")}
          </Button>
        </Banner>
      )}
      {restored && dirty && conflictVersion === null && !delegationConflict && (
        <Banner tone="info" title={t("businesses.rules.restored.title")}>
          <p style={{ margin: "0 0 var(--space-2)" }}>{t("businesses.rules.restored.body")}</p>
          <Button variant="secondary" size="sm" disabled={busy} onClick={discard}>
            {t("businesses.rules.discard")}
          </Button>
        </Banner>
      )}
      {issues.length > 0 && (
        <Banner tone="danger" title={t("businesses.rules.invalid.title")}>
          {t("businesses.rules.invalid.body")}
        </Banner>
      )}
      {saveError && <Banner tone="danger" title={t("common.error")}>{saveError}</Banner>}

      <div className="stack-sm">
        {data.parameters.map((definition) => {
          const key = definition.key;
          const value = draft[key];
          const errors = fieldIssues(key).map((issue) => issueText(issue, definition));
          if (business) return <BusinessRuleValueField key={key} definition={definition} value={value}
            grant={delegation?.keys[key]} readOnly={readOnly} busy={busy} errors={errors}
            invariants={data.invariants.filter((invariant) => invariant.keys.includes(key))
              .map((invariant) => t(`businesses.rules.invariant.${invariant.id}`, undefined, invariant.id))}
            onChange={(next) => edit(key, next)} />;
          const isDefault = valueOf(definition, value) === data.defaults[key];
          const boundText = definition.value_type === "integer"
            ? (definition.max_inclusive !== undefined && definition.max_inclusive < Number.MAX_SAFE_INTEGER
              ? t("businesses.rules.bounds.between", { min: ruleValueNumber(definition.min_inclusive ?? 0, definition.unit), max: ruleValueNumber(definition.max_inclusive, definition.unit) })
              : t("businesses.rules.bounds.atLeast", { min: ruleValueNumber(definition.min_inclusive ?? 0, definition.unit) }))
            : null;
          const defaultText = typeof definition.default_value === "boolean"
            ? t(definition.default_value ? "businesses.rules.yes" : "businesses.rules.no")
            : String(ruleValueNumber(definition.default_value, definition.unit));
          const helper = [
            definition.description[lang] ?? definition.description.en,
            [t("businesses.rules.default", { value: defaultText }), boundText].filter(Boolean).join(" · "),
          ].join(" ");
          const inputId = `rule-param-${key}`;
          return (
            <div className="reqventos-rule-parameter" key={key} data-testid={`rule-param-${key}`}>
              <div className="reqventos-rule-parameter__head">
                <StatusPill
                  tone={RISK_TONE[definition.risk] ?? "warning"}
                  label={t(`businesses.rules.risk.${definition.risk}`, undefined, definition.risk)}
                />
                {!isDefault && !readOnly && (
                  <button
                    type="button"
                    className="linkbtn"
                    disabled={busy}
                    onClick={() => edit(key, typeof definition.default_value === "boolean"
                      ? definition.default_value
                      : String(ruleValueNumber(definition.default_value, definition.unit)))}
                  >
                    {t("businesses.rules.useDefault")}
                  </button>
                )}
              </div>
              {definition.value_type === "boolean" ? (
                <FormField label={labelOf(key)} htmlFor={inputId} helper={helper} error={errors.join(" ") || undefined}>
                  <Switch
                    id={inputId}
                    checked={value === true}
                    disabled={busy || readOnly}
                    onLabel={t("businesses.rules.yes")}
                    offLabel={t("businesses.rules.no")}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      edit(key, event.currentTarget.checked)}
                  />
                </FormField>
              ) : (
                <FormField label={labelOf(key)} htmlFor={inputId} helper={helper} error={errors.join(" ") || undefined}>
                  <Input
                    id={inputId}
                    type="number"
                    inputMode={definition.unit === "eur_cents" ? "decimal" : "numeric"}
                    min={definition.min_inclusive === undefined ? undefined : ruleValueNumber(definition.min_inclusive, definition.unit)}
                    step={ruleValueNumber(definition.step ?? 1, definition.unit)}
                    value={String(value)}
                    disabled={busy || readOnly}
                    className="reqventos-rule-parameter__input"
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      edit(key, event.currentTarget.value)}
                  />
                </FormField>
              )}
            </div>
          );
        })}
      </div>

      {!business && data.invariants.map((invariant) => (
        <p className="subtle reqventos-rule-parameters__invariant" key={invariant.id}>
          {t(`businesses.rules.invariant.${invariant.id}`, undefined, invariant.id)}
        </p>
      ))}
      <p className="subtle reqventos-rule-parameters__note">{t("businesses.rules.openRequests")}</p>

      {!readOnly && (
        <div className={`management-actions ${business ? "reqventos-rule-values-savebar" : ""}`}>
          {dirty && <span className="subtle reqventos-rule-parameters__unsaved">{t(business ? "ruleValues.unsaved" : "businesses.rules.unsaved", { count: Object.keys(patch).length })}</span>}
          <Button variant="ghost" disabled={busy || !dirty} onClick={discard}>
            {t("businesses.rules.discard")}
          </Button>
          {/* After a conflict only a reload helps: saving again would resend the stale version. */}
          <Button variant="primary" loading={busy} disabled={busy || !dirty || conflictVersion !== null || delegationConflict || remoteChanged} onClick={business ? check : save}>
            {t(business ? "ruleValues.checkSave" : "businesses.rules.save")}
          </Button>
        </div>
      )}

      </div>
      {!business && <div id="rule-panel-delegation" role="tabpanel" aria-labelledby="rule-tab-delegation" hidden={tab !== "delegation"} className="reqventos-rule-panel">
        {delegationError && <Banner tone="danger" title={t("common.error")}>
          <p>{delegationError}</p>
          <Button variant="secondary" onClick={() => { void reloadDelegation(); }}>{t("businesses.rules.delegation.reload")}</Button>
        </Banner>}
        {delegation ? <TenantRuleDelegation tenantId={tenantId} values={data} data={delegation}
          remoteChanged={remoteChanged} onEditStateChange={delegationEditChanged}
          onSaved={setDelegation} reload={reloadDelegation} refreshValues={() => { void load(false); }} pushToast={pushToast} />
          : !delegationError && <p className="subtle" aria-busy="true">{t("common.loading")}</p>}
      </div>}
      <div id="rule-panel-history" role="tabpanel" aria-labelledby="rule-tab-history" hidden={tab !== "history"} className="reqventos-rule-panel">
        {delegationError && <Banner tone="danger" title={t("common.error")}>
          <p>{delegationError}</p>
          <Button variant="secondary" onClick={() => { void reloadDelegation(); }}>{t("businesses.rules.delegation.reload")}</Button>
        </Banner>}
        {delegation ? <TenantRuleHistory values={data} delegation={delegation} />
          : !delegationError && <p className="subtle" aria-busy="true">{t("common.loading")}</p>}
      </div>
      {preview && <RuleValuesPreviewDialog data={data} preview={preview.result} busy={busy}
        onClose={() => setPreview(null)} onSave={save} />}
    </div>
  );
}
